"use client";

import type { JSX } from "react";
import { Image, Input, Text, View } from "@/components";
import type { AttemptQuestion } from "@/types/elearning";
import { resolveFileSrc } from "@/utils/elearning/fileUrl";

export interface QuestionRendererProps {
  /** Pregunta del intento (sin respuestas correctas: no viajan al cliente). */
  pregunta: AttemptQuestion;
  /** IDs de las opciones actualmente elegidas. */
  seleccion: string[];
  /** Cambio de selección: la página persiste vía autosave. */
  onChange: (optionIds: string[]) => void;
  /** Deshabilita la edición (intento vencido o enviándose). */
  disabled?: boolean;
}

/**
 * @name QuestionRenderer
 * @description Renderizador polimórfico de preguntas del intento (A6). Todos los
 * tipos del dominio se resuelven sobre opciones: única/verdadero-falso/señal/
 * interpretación como radio y múltiple como checkbox. Las de señal e
 * interpretación agregan la imagen (con alt genérico que no revela la respuesta).
 * El orden de preguntas y opciones llega ya aleatorizado del servidor y se
 * respeta tal cual.
 *
 * @param {QuestionRendererProps} props - Pregunta, selección actual y callback.
 * @returns {JSX.Element} La pregunta renderizada.
 */
const QuestionRenderer = ({
  pregunta,
  seleccion,
  onChange,
  disabled = false
}: QuestionRendererProps): JSX.Element => {
  const multiple = pregunta.type === "multipleChoice";

  const toggle = (optionId: string) => {
    if (disabled) return;
    if (multiple) {
      onChange(
        seleccion.includes(optionId)
          ? seleccion.filter((id) => id !== optionId)
          : [...seleccion, optionId]
      );
    } else {
      onChange([optionId]);
    }
  };

  return (
    <View role="group" className="flex flex-col gap-4">
      <Text
        type="p"
        id={`statement-${pregunta.questionId}`}
        className="text-base font-medium text-foreground"
      >
        {pregunta.statement}
      </Text>

      {pregunta.imageUrl && (
        <View className="flex justify-center">
          <Image
            src={resolveFileSrc(pregunta.imageUrl)}
            alt=""
            width={320}
            height={240}
            unoptimized
            className="rounded-xl object-contain max-h-60 !w-auto"
          />
        </View>
      )}

      <View className="flex flex-col gap-2">
        {pregunta.options.map((opcion) => {
          const elegida = seleccion.includes(opcion.optionId);
          const inputId = `${pregunta.questionId}-${opcion.optionId}`;
          return (
            <Text
              key={opcion.optionId}
              type="label"
              htmlFor={inputId}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                elegida
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <Input
                id={inputId}
                type={multiple ? "checkbox" : "radio"}
                name={pregunta.questionId}
                checked={elegida}
                onChange={() => toggle(opcion.optionId)}
                disabled={disabled}
                className="accent-primary w-4 h-4 shrink-0"
              />
              <Text type="span" className="text-sm text-foreground">
                {opcion.text}
              </Text>
            </Text>
          );
        })}
      </View>
    </View>
  );
};

export default QuestionRenderer;
